import React, {
  ReactNode,
  RefObject,
  TouchEventHandler,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { css, useTheme } from "styled-components";
import {
  animate,
  AnimationPlaybackControls,
  motion,
  useMotionValueEvent,
  useScroll,
} from "motion/react";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import useMeasure from "utils/hooks/useMeasure";
import { SwipeFiltersContext } from "common/Filters/common/SwipeFilters.context.ts";
import { PageTabsContext } from "common/Tabs/PageTabs.context.ts";
import useDebouncedCallback from "@shared/utils/hooks/useDebouncedCallback.ts";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay.tsx";
import { getLocaleStringWithPrecision, round } from "@shared/utils/numbers.ts";

import { PAGE_TAB_ID, PAGE_TAB_INDICATOR_ID, PAGE_TABS_BAR_ID } from "testIds";
import { phoneMaxWidth } from "common/NewLook/layout.ts";
import { getNewLookTokens } from "common/NewLook/tokens.ts";
import useTabIndicator, {
  getTabElements,
  TAB_ITEM_CLASS,
} from "common/Tabs/useTabIndicator.ts";
import useStickyPageTabs from "common/Tabs/useStickyPageTabs.ts";

const MAX_TAB_COUNT = 99999;

interface PageTabsProps {
  defaultKey?: string;
  items: TabItem[];
  commonContent?: ReactNode;
  onTabChange?: (tab: TabItem) => void;
  hideTabs?: boolean;
  unmounting?: boolean;
  isInstant?: boolean;
  urlChangeWait?: number;
  scrollRef?: RefObject<HTMLElement | null>;
  stickyTop?: number;
}

const PageTabs = ({
  defaultKey,
  items,
  commonContent,
  onTabChange,
  hideTabs,
  unmounting,
  isInstant,
  urlChangeWait,
  scrollRef,
  stickyTop = 0,
}: PageTabsProps) => {
  const history = useHistory();
  const location = useLocation();
  const { isNewLook } = useTheme();
  const isUrlTabs = items.every((item) => !!item.link);
  const { swipeActiveRef } = useContext(SwipeFiltersContext) || {};
  const isInstantUrlChange = urlChangeWait === 0;

  const checkPathMatch = (path: string) => {
    return matchPath(location.pathname, {
      path,
      exact: false,
      strict: false,
    });
  };

  const urlTab = useMemo(
    () =>
      isUrlTabs ? items.find((item) => checkPathMatch(item.link!)) : undefined,
    [items, location.pathname]
  );
  const defaultKeyTab = useMemo(
    () => items.find((item) => item.key === defaultKey),
    [items, defaultKey]
  );
  const defaultTab = useMemo(() => {
    return defaultKeyTab ?? urlTab ?? items?.[0];
  }, [items, defaultKeyTab?.key, urlTab?.key]);

  const [selectedKey, setSelectedKey] = useState<string | undefined>(
    () => defaultTab?.key
  );

  const tabListRef = useRef<HTMLDivElement>(null);
  const tabsBarRef = useRef<HTMLDivElement>(null);
  const tabPanelsRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<AnimationPlaybackControls | null>(null);
  const initializedRef = useRef(false);
  const changeUrlRef = useRef<NodeJS.Timeout | null>(null);
  const lastChangeUrlLocationRef = useRef<string | null>(null);
  const currentLocationRef = useRef<string | null>(null);
  currentLocationRef.current = location.pathname;
  const numTabs = items?.length ?? 0;
  const tabContentSize = useMeasure<HTMLDivElement | null>(tabPanelsRef)[1];
  const roundedTabContentSize = round(tabContentSize.width, 1);

  const { scrollXProgress } = useScroll({
    container: tabPanelsRef,
  });
  const [visitedTabs, setVisitedTabs] = useState<Set<string>>(new Set());
  const [tabKeyToCount, setTabKeyToCount] = useState<
    Record<string, number | undefined>
  >({});

  const tabsKey = items.map((item) => item.key).join("_");
  const isSticky = !!scrollRef;
  // New look: which edges of the tab row have more tabs past them.
  const [tabsOverflow, setTabsOverflow] = useState({
    start: false,
    end: false,
  });

  const updateTabsOverflow = useCallback(() => {
    const tabList = tabListRef.current;
    if (!isNewLook || !tabList) return;
    const start = tabList.scrollLeft > 1;
    const end =
      tabList.scrollLeft + tabList.clientWidth < tabList.scrollWidth - 1;
    setTabsOverflow((prev) =>
      prev.start === start && prev.end === end ? prev : { start, end }
    );
  }, [isNewLook]);

  useEffect(() => {
    updateTabsOverflow();
  }, [updateTabsOverflow, tabsKey, roundedTabContentSize, tabKeyToCount]);

  // New look: keep the selected tab fully in view when the row scrolls.
  const itemsRef = useRef(items);
  itemsRef.current = items;
  useEffect(() => {
    const tabList = tabListRef.current;
    if (!isNewLook || !tabList || tabList.scrollWidth <= tabList.clientWidth) {
      return;
    }
    const index = itemsRef.current.findIndex(
      (item) => item.key === selectedKey
    );
    const tab = tabList.querySelectorAll<HTMLElement>(`.${TAB_ITEM_CLASS}`)[
      index
    ];
    if (!tab) return;
    const left = tab.offsetLeft - TABS_EDGE_FADE;
    const right =
      tab.offsetLeft + tab.offsetWidth + TABS_EDGE_FADE - tabList.clientWidth;
    if (tabList.scrollLeft > left) {
      tabList.scrollTo({ left, behavior: "smooth" });
    } else if (tabList.scrollLeft < right) {
      tabList.scrollTo({ left: right, behavior: "smooth" });
    }
  }, [isNewLook, selectedKey, tabsKey]);

  useEffect(() => {
    return () => {
      clearChangeUrl();
    };
  }, []);

  useEffect(() => {
    if (selectedKey && !visitedTabs.has(selectedKey)) {
      visitedTabs.add(selectedKey);
    }
  }, [selectedKey]);

  const handleDefaultTabChange = () => {
    scrollTabsContent(defaultTab, true);
    setSelectedKey(defaultTab?.key);
  };

  useEffect(() => {
    if (urlTab && defaultTab?.key !== selectedKey) {
      handleDefaultTabChange();
    }
  }, [urlTab?.key]);

  useEffect(() => {
    if (defaultTab?.key !== selectedKey) {
      handleDefaultTabChange();
    }
  }, [defaultKeyTab?.key ?? items?.[0].key]);

  useEffect(() => {
    if (!roundedTabContentSize) return;
    initializedRef.current = false;
    onTabChange?.(defaultTab);
    handleDefaultTabChange();
    setVisitedTabs(new Set([defaultTab?.key]));
    initializedRef.current = true;
  }, [tabsKey, roundedTabContentSize]);

  const getTabIndex = useCallback(
    (x: number, round?: boolean) => {
      const baseValue = (numTabs - 1) * x;
      const roundedValue = round
        ? Math.round(baseValue)
        : Math.floor(baseValue);
      return Math.max(0, roundedValue);
    },
    [items]
  );

  const clearChangeUrl = () => {
    if (changeUrlRef.current) {
      clearTimeout(changeUrlRef.current);
    }
  };

  const changeTabUrl = (item: TabItem) => {
    clearChangeUrl();
    if (isUrlTabs) {
      if (isInstantUrlChange) {
        history.push(item.link!);
      } else {
        changeUrlRef.current = setTimeout(() => {
          if (currentLocationRef.current === lastChangeUrlLocationRef.current) {
            history.push(item.link!);
          }
        }, urlChangeWait ?? 1000);
      }
    }
  };

  const { x, width } = useTabIndicator(
    tabListRef,
    scrollXProgress,
    `${tabsKey}-${hideTabs}`
  );

  useStickyPageTabs({
    scrollRef,
    tabsBarRef,
    pagerRef: tabPanelsRef,
    activeIndex: items.findIndex((item) => item.key === selectedKey),
    stickyTop,
    panelsKey: tabsKey,
  });

  const handleScrollSettled = useDebouncedCallback({
    callback: (tab: TabItem) => {
      if (!isInstant) onTabChange?.(tab);
      if (!isInstantUrlChange) changeTabUrl(tab);
    },
    wait: 500,
  });

  const addVisitedTab = (tabKey: string) => {
    setVisitedTabs((prev) => {
      const newSet = new Set(prev);
      newSet.add(tabKey);
      return newSet;
    });
  };

  const debouncedAddVisitedTab = useDebouncedCallback({
    callback: addVisitedTab,
    wait: 400,
  });

  const addVisitedTabWithDebounce = (tab: TabItem) => {
    const { key } = tab;
    if (visitedTabs.has(key) || tab.forceRender) {
      addVisitedTab(key);
    } else {
      debouncedAddVisitedTab(key);
    }
  };

  useMotionValueEvent(scrollXProgress, "change", (x) => {
    if (
      animationRef.current ||
      !getTabElements(tabListRef.current).length ||
      !initializedRef.current
    )
      return;
    const tab = items[getTabIndex(x, true)];
    if (tab.key !== selectedKey && !unmounting && tabContentSize.width) {
      setSelectedKey(tab.key);
      addVisitedTabWithDebounce(tab);
      lastChangeUrlLocationRef.current = currentLocationRef.current;
      clearChangeUrl();
      handleScrollSettled(tab);
      if (isInstant) onTabChange?.(tab);
      if (isInstantUrlChange) changeTabUrl(tab);
    }
  });

  const scrollTabsContent = (item: TabItem, immediate?: boolean) => {
    const tabPanel = tabPanelsRef.current;
    if (
      (!immediate && scrollXProgress.getVelocity() && !animationRef.current) ||
      !tabPanel
    ) {
      return;
    }

    const index = items?.findIndex((tab) => tab.key === item.key) ?? 0;
    const scrollTo = roundedTabContentSize * numTabs * (index / numTabs);
    if (immediate) {
      tabPanel.scrollLeft = scrollTo;
      return;
    }
    animationRef.current?.stop();
    animationRef.current = animate(tabPanel.scrollLeft, scrollTo, {
      type: "spring",
      bounce: 0,
      duration: 0.6,
      onUpdate: (v) => {
        tabPanel.scrollLeft = v;
      },
      onPlay: () => {
        tabPanel.style.scrollSnapType = "none";
        clearChangeUrl();
      },
      onComplete: () => {
        tabPanel.style.scrollSnapType = "x mandatory";
        animationRef.current = null;
        lastChangeUrlLocationRef.current = currentLocationRef.current;
        clearChangeUrl();
        changeTabUrl(item);
      },
    });
  };

  const onTabClick = (item: TabItem) => {
    setSelectedKey(item.key);
    if (isInstantUrlChange) {
      changeTabUrl(item);
    }
    onTabChange?.(item);
    addVisitedTabWithDebounce(item);
    scrollTabsContent(item);
  };

  const onTouchMove: TouchEventHandler<HTMLDivElement> = useCallback(() => {
    if (tabPanelsRef.current !== null && swipeActiveRef?.current) {
      tabPanelsRef.current.style.overflowX = "hidden";
    }
  }, []);

  const onTouchEnd: TouchEventHandler<HTMLDivElement> = useCallback(() => {
    if (tabPanelsRef.current !== null) {
      tabPanelsRef.current.style.overflowX = "scroll";
    }
  }, []);

  const setTabCount = (tabKey: string, count: number | undefined) => {
    setTabKeyToCount((prev) => ({ ...prev, [tabKey]: count }));
  };

  const contentWrapperStyle = useMemo(
    () => (hideTabs ? ({ overflowX: "hidden" } as const) : undefined),
    [hideTabs]
  );

  const contextValue = useMemo(() => {
    return { setTabCount, isPageTabs: true };
  }, []);

  const tabTextItems = items?.map((item) => {
    let label = item.label;
    const numItems =
      tabKeyToCount && item.key in tabKeyToCount
        ? tabKeyToCount[item.key]
        : undefined;
    if (numItems !== undefined) {
      label = (
        <>
          {label}
          <span style={{ marginLeft: 3 }}>
            ({formatTabCount(numItems, isNewLook)})
          </span>
        </>
      );
    }

    return (
      <TabItem
        key={item.key}
        type="button"
        data-testid={PAGE_TAB_ID}
        aria-pressed={item.key === selectedKey}
        $selected={item.key === selectedKey}
        className={TAB_ITEM_CLASS}
        onClick={() => onTabClick(item)}
        $numItems={items.length}
      >
        {label}
      </TabItem>
    );
  });

  return (
    <PageTabsContext.Provider value={contextValue}>
      <Wrapper $sticky={isSticky}>
        {!hideTabs && !isNewLook && (
          <TabsWrapper ref={tabListRef} $hide={!tabContentSize.width}>
            {tabTextItems}
            <TabActiveIndicator
              style={{ x, width }}
              data-testid={PAGE_TAB_INDICATOR_ID}
            />
          </TabsWrapper>
        )}
        {!hideTabs && isNewLook && (
          <NewLookTabsBar
            ref={tabsBarRef}
            $fadeStart={tabsOverflow.start}
            $fadeEnd={tabsOverflow.end}
            $stickyTop={isSticky ? stickyTop : undefined}
            data-testid={PAGE_TABS_BAR_ID}
          >
            <TabsWrapper
              ref={tabListRef}
              $hide={!tabContentSize.width}
              onScroll={updateTabsOverflow}
            >
              {tabTextItems}
              <TabActiveIndicator
                style={{ x, width }}
                data-testid={PAGE_TAB_INDICATOR_ID}
              />
            </TabsWrapper>
          </NewLookTabsBar>
        )}
        {commonContent}
        <ContentWrapper
          ref={tabPanelsRef}
          onTouchMove={hideTabs ? undefined : onTouchMove}
          onTouchEnd={hideTabs ? undefined : onTouchEnd}
          style={contentWrapperStyle}
          $sticky={isSticky}
        >
          {items?.map((item) => {
            const shouldRender =
              item.forceRender ||
              visitedTabs.has(item.key) ||
              selectedKey === item.key;
            const content = shouldRender ? (
              item.children
            ) : (
              <LoadingSpinnerOverlay
                isLoading={true}
                hideContentWhileLoading={true}
                spinnerSize="large"
              />
            );
            return <TabContent key={item.key}>{content}</TabContent>;
          })}
        </ContentWrapper>
      </Wrapper>
    </PageTabsContext.Provider>
  );
};

export default PageTabs;

const formatTabCount = (count: number, isNewLook: boolean) => {
  const shown = Math.min(count, MAX_TAB_COUNT);
  const text = isNewLook ? getLocaleStringWithPrecision(shown, 0) : shown;
  return count > MAX_TAB_COUNT ? `${text}+` : text;
};

export interface TabItem {
  key: string;
  label: string | React.JSX.Element;
  link?: string;
  routerMatch?: string;
  children?: React.JSX.Element;
  forceRender?: boolean;
}

export const PAGE_TABS_HEIGHT = 32;
/** Width of the fade that hints at more tabs past an edge (new look). */
const TABS_EDGE_FADE = 24;

const Wrapper = styled.div<{ $sticky: boolean }>`
  flex: 1;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  ${(p) =>
    p.$sticky &&
    css`
      flex: none;
      height: auto;
    `}
`;
const TabsWrapper = styled.div<{ $hide?: boolean }>`
  display: flex;
  border-bottom: 2px solid ${(p) => p.theme.colors.lightestBorderGrey};
  position: relative;
  height: ${PAGE_TABS_HEIGHT}px;
  overflow-x: auto;
  overflow-y: hidden;
  &::-webkit-scrollbar {
    display: none;
  }
  ${(p) => p.$hide && "opacity: 0;"};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      border-bottom: ${p.theme.borders.lightest};
      padding: 0 8px;
      scrollbar-width: none;
      /* Room between the label and the underline below it. */
      height: 46px;
      @media (max-width: ${phoneMaxWidth}) {
        /* Phone: tighter stacked chrome (header + tabs + filters); the
           first label's text lines up with the 16px gutter. */
        height: 36px;
        padding: 0 6px;
      }
    `}
`;
/**
 * New look: the tab row lines up with the 16px page gutter, and when the
 * tabs don't fit, a soft fade on the clipped edge shows the row scrolls.
 */
const NewLookTabsBar = styled.div<{
  $fadeStart: boolean;
  $fadeEnd: boolean;
  $stickyTop?: number;
}>`
  position: relative;
  ${(p) =>
    p.$stickyTop !== undefined &&
    css`
      position: sticky;
      top: ${p.$stickyTop}px;
      z-index: 2;
      background: ${p.theme.colors.layoutBgColor};
    `}
  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 1px;
    width: ${TABS_EDGE_FADE}px;
    z-index: 2;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.15s ease;
  }
  &::before {
    left: 0;
    background: linear-gradient(
      to right,
      ${(p) => p.theme.colors.layoutBgColor},
      transparent
    );
    ${(p) => p.$fadeStart && "opacity: 1;"}
  }
  &::after {
    right: 0;
    background: linear-gradient(
      to left,
      ${(p) => p.theme.colors.layoutBgColor},
      transparent
    );
    ${(p) => p.$fadeEnd && "opacity: 1;"}
  }
`;
const TabItem = styled.button<{ $selected?: boolean; $numItems?: number }>`
  padding: 10px 12px;
  border: 0;
  background: transparent;
  font: inherit;
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.primaryBlue};
    outline-offset: -2px;
  }
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) =>
    p.$selected ? p.theme.colors.primaryBlue : p.theme.colors.primaryText};
  white-space: nowrap;
  font-size: ${(p) => ((p.$numItems ?? 0) >= 5 ? 12 : 14)}px;
  @media (max-width: ${phoneMaxWidth}) {
    padding: 0 8px 4px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      flex: 1 0 auto;
      padding: 0 12px;
      @media (max-width: 600px) {
        padding: 0 10px;
      }
      color: ${p.$selected
        ? getNewLookTokens(p.theme).accent
        : p.theme.colors.secondaryText};
      font-size: ${(p.$numItems ?? 0) >= 5 ? 13 : 14}px;
      font-weight: ${p.$selected ? 500 : 450};
      @media (max-width: ${phoneMaxWidth}) {
        /* Phone: never shrink labels; long strips scroll behind the fade. */
        font-size: 14px;
      }
      cursor: pointer;
      transition: color 0.15s ease, background-color 0.15s ease;
      &:hover {
        color: ${p.$selected
          ? getNewLookTokens(p.theme).accent
          : p.theme.colors.titleColor};
      }
      &:focus-visible {
        outline: 2px solid ${getNewLookTokens(p.theme).focus};
        outline-offset: -4px;
        border-radius: 8px;
      }
    `}
`;
const TabActiveIndicator = styled(motion.span)`
  will-change: transform;
  position: absolute;
  left: 0;
  bottom: 0;
  background-color: ${(p) => p.theme.colors.primaryBlue};
  height: 2px;
  z-index: 1;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      height: 3px;
      border-radius: 3px 3px 0 0;
      @media (max-width: ${phoneMaxWidth}) {
        height: 2px;
        border-radius: 2px 2px 0 0;
      }
    `}
`;
const ContentWrapper = styled.div<{ $sticky: boolean }>`
  width: 100%;
  flex: 1;
  display: flex;
  overflow-x: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
  scroll-snap-type: x mandatory;
  ${(p) =>
    p.$sticky &&
    css`
      align-items: flex-start;
      overflow-y: hidden;
    `}
`;
const TabContent = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 100%;
  scroll-snap-align: center;
  scroll-snap-stop: always;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;
